/**
 * @description
 * @param x
 * @param y
 * @returns
 */
function calculateCovarianceMatrix(x: number[], y: number[]): number[][] {
  const xMean = x.reduce((a, b) => a + b, 0) / x.length;
  const yMean = y.reduce((a, b) => a + b, 0) / y.length;
  const length = x.length;

  let covXX = 0;
  let covYY = 0;
  let covXY = 0;

  for (let i = 0; i < length; i++) {
    covXX += Math.pow(x[i] - xMean, 2);
    covYY += Math.pow(y[i] - yMean, 2);
    covXY += (x[i] - xMean) * (y[i] - yMean);
  }

  covXX /= length - 1;
  covYY /= length - 1;
  covXY /= length - 1;

  return [
    [covXX, covXY],
    [covXY, covYY],
  ];
}

class EigenValueVector {
  value: number;
  vector: number[];
  constructor(value: number, vector: number[]) {
    this.value = value;
    this.vector = vector;
  }
}

/**
 * @description calculates eigan values and vectors
 * @param matrix
 * @returns
 */
function calculateEiganValuesAndVectors(
  matrix: number[][]
): EigenValueVector[] {
  const a = matrix[0][0];
  const b = matrix[0][1];
  const c = matrix[1][0];
  const d = matrix[1][1];

  const discriminant = Math.sqrt(Math.pow(a - d, 2) + 4 * b * c);
  const eigenValue1 = (a + d + discriminant) / 2;
  const eigenValue2 = (a + d - discriminant) / 2;

  const eigenVector1X = eigenValue1 - a;
  const eigenVector1Y = b;

  const eigenVector2X = eigenValue2 - a;
  const eigenVector2Y = b;

  return [
    new EigenValueVector(eigenValue1 * 3, [eigenVector1X, eigenVector1Y]),
    new EigenValueVector(eigenValue2 * 3, [eigenVector2X, eigenVector2Y]),
  ];
}

function calculateEllipsePoints(
  centerX: number,
  centerY: number,
  stdDevX: number,
  stdDevY: number,
  angle: number,
  numberOfPoints: number
): { x: number; y: number }[] {
  const points: { x: number; y: number }[] = [];
  for (let i = 0; i < numberOfPoints; i++) {
    const angleRad = (i * 2 * Math.PI) / numberOfPoints;
    const x =
      centerX +
      stdDevX * Math.cos(angleRad) * Math.cos(angle) -
      stdDevY * Math.sin(angleRad) * Math.sin(angle);
    const y =
      centerY +
      stdDevX * Math.cos(angleRad) * Math.sin(angle) +
      stdDevY * Math.sin(angleRad) * Math.cos(angle);
    points.push({ x, y });
  }
  return points;
}

/**
 * @description calculates the 95% confidence ellipse
 * @param points
 * @returns
 */
export function nineFiveConfidenceEllipse(
  points: { x: number; y: number }[]
): { x: number; y: number }[] {
  let x: number[] = [];
  let y: number[] = [];
  for (let i = 0; i < points.length; i++) {
    x.push(points[i].x);
    y.push(points[i].y);
  }

  const covarianceMatrix = calculateCovarianceMatrix(x, y);
  const eigenValuesAndVectors =
    calculateEiganValuesAndVectors(covarianceMatrix);

  const eigenValue1 = eigenValuesAndVectors[0].value;
  const eigenVector1 = eigenValuesAndVectors[0].vector;
  const eigenValue2 = eigenValuesAndVectors[1].value;

  const stdDevX = Math.sqrt(eigenValue1);
  const stdDevY = Math.sqrt(eigenValue2);

  const angle = Math.atan2(eigenVector1[0], eigenVector1[1]);

  const centerX = x.reduce((a, b) => a + b, 0) / x.length;
  const centerY = y.reduce((a, b) => a + b, 0) / y.length;

  return calculateEllipsePoints(centerX, centerY, stdDevX, stdDevY, angle, 120);
}
